import { NULL_TIMESPAN } from '@src/suite/common/enums/constants';
import { DateTime, Duration } from 'luxon';

import { formatDurationLikeMoment, isBlankLuxonDate } from './date-utils';

/**
 * Calculates the difference in Daylight Saving Time (DST or Summer Time)
 * offsets between two dates and returns the difference in minutes.
 * @param firstDate The first date to compare.
 * @param secondDate The second date to compare.
 * @returns The difference in minutes between the two dates' DST offsets.
 */
export const calculateDurationDSTDifference = (firstDate: DateTime, secondDate: DateTime): number => {
    return firstDate.offset - secondDate.offset;
};

export const getStoryDuration = (rundown: StorytellingRundownRecord, currentId: string, isRoTimeEstimated: boolean, estimatedTimeThreshold: number) => {
    if (!rundown?.children) return;
    const currentStoryIdx = rundown?.children?.findIndex(story => story.Guid === currentId);
    const currentStory = rundown?.children[currentStoryIdx];
    let ts = Duration.fromMillis(0);
    // TODO: Use setting for global separator
    const slug = currentStory?.Title?.split('-')[0];
    for (let i = currentStoryIdx; i < rundown?.children?.length; i++) {
        const story = rundown?.children[i];
        if (story?.Guid !== '0') {
            const storySlug = story?.Title?.split('-')[0];
            if (slug === storySlug) {
                if (!story?.Float) {
                    ts = ts.plus(getSegmentTime(story, isRoTimeEstimated, estimatedTimeThreshold));
                }
            } else {
                return ts;
            }
        }
    }
    return ts;
};

export const getSegmentTime = (script: StorytellingStoryRecord, isRoTimeEstimated: boolean, estimatedTimeThreshold: number): Duration => {
    if (!script) return Duration.fromMillis(0);
    const float = script?.Float;
    const cBreak = script?.CBreak;

    if (float || cBreak) {
        return Duration.fromMillis(0);
    }

    let tsActual = Duration.fromMillis(0);
    let tsReadTime = Duration.fromMillis(0);
    let tsEstimated = Duration.fromMillis(0);

    if (script?.isActualEnteredManually) {
        tsActual = script?.Actual;
        tsReadTime = script?.ReadTime;
        tsEstimated = script?.Estimated;
    } else {
        tsReadTime = script?.ReadTime || tsReadTime;
        tsEstimated = script?.Estimated || tsEstimated;
    }

    if (!isRoTimeEstimated) {
        if (!script?.isActualEnteredManually) {
            if (tsReadTime.toMillis() > estimatedTimeThreshold) {
                return tsReadTime;
            } else {
                return tsEstimated;
            }
        } else {
            return tsActual;
        }
    } else {
        return tsEstimated;
    }
};

export const getFrontTimings = (
    rundown: StorytellingRundownRecord,
    _hitTimeChanged: boolean,
    estimatedTimeThreshold: number,
    isRoTimeEstimated: boolean,
    languageSettings: Record<string, string>,
    isFirstRun: boolean
) => {
    if (!rundown?.children) return;
    if (!rundown?.TimingObj) return;
    let _diffTotalOverUnderTime = 0.0;
    const prevTotalOverUnderTime = rundown.TimingObj?.TotalOverUnderTime?.as('seconds') || 0.0;
    let totalReadTimeCume = 0.0;
    let totalOverUnderTime = 0.0;
    let lastTime = 0.0;
    let frontHitCume = 0.0;
    let lastFrontTime = DateTime.now();
    let currentIDIndex = -1;
    let lastHardHitTimeRowIndex = -1;
    let firstHardHitTimeRowIndexAfterTimingBar = -1;
    const datStartTime = isBlankLuxonDate(rundown.StartTime) ? DateTime.now() : rundown.StartTime;
    const datEndTime = isBlankLuxonDate(rundown.EndTime) ? DateTime.now() : rundown.EndTime;
    let datHitTime = rundown.HitTime;
    const roDur = rundown.RundownDuration.as('seconds');
    const isLtt = rundown.LTTRecording;
    const isOnAir = rundown.OnAir;
    const currentId = rundown.CurrentID;
    if (!datHitTime || isBlankLuxonDate(datHitTime) || !(isOnAir || isLtt)) {
        datHitTime = datStartTime;
    }
    let isCurrentTimingStory = false;
    const children = rundown.children as StorytellingStoryRecord[];
    for (let i = 0; i < children.length; i++) {
        let story = children[i] as StorytellingStoryRecord;
        if (story.Guid === currentId && !isCurrentTimingStory) {
            isCurrentTimingStory = true;
        }
        if (story.Guid === '0') {
            break;
        }
        if (story.Guid === currentId) {
            currentIDIndex = i;
            lastFrontTime = datHitTime;
        }
        const ret = frontTimeRow(
            rundown,
            story,
            isRoTimeEstimated,
            estimatedTimeThreshold,
            currentId,
            isCurrentTimingStory,
            totalReadTimeCume,
            datHitTime,
            totalOverUnderTime,
            frontHitCume,
            lastFrontTime,
            lastTime,
            languageSettings
        );
        totalReadTimeCume = ret.totalReadTimeCume;
        totalOverUnderTime = ret.totalOverUnderTime;
        frontHitCume = ret.frontHitCume;
        lastFrontTime = ret.lastFrontTime;
        lastTime = ret.lastTime;
        story = ret.story;
        const hardHitFront = story.HardHitFront;
        if (hardHitFront && hardHitFront.year === 1899) {
            lastHardHitTimeRowIndex = i;
            if (currentIDIndex >= 0 && i > currentIDIndex && firstHardHitTimeRowIndexAfterTimingBar < 0) {
                firstHardHitTimeRowIndexAfterTimingBar = i;
                rundown.TimingObj.FrontOverUnder = story.FrontOverUnder;
            }
        }
        rundown.children[i] = story;
    }

    if (firstHardHitTimeRowIndexAfterTimingBar < 0) {
        if (rundown?.TimingObj) {
            rundown.TimingObj = {
                ...rundown.TimingObj,
                FrontOverUnder: NULL_TIMESPAN,
            };
        }
    }

    _diffTotalOverUnderTime = prevTotalOverUnderTime - totalOverUnderTime;
    let tempOverUnder = Duration.fromMillis(0);
    if (isOnAir || isLtt) {
        tempOverUnder = Duration.fromObject({ seconds: totalOverUnderTime });
    } else {
        tempOverUnder = Duration.fromObject({ seconds: totalReadTimeCume });
    }

    if ((currentIDIndex === 0 && lastHardHitTimeRowIndex > -1) || !rundown.TimingObj.IsTiming || isFirstRun) {
        if (isLtt) {
            tempOverUnder = tempOverUnder.minus({ seconds: roDur });
        } else if (lastHardHitTimeRowIndex > -1) {
            let cumeAfterLastHardHit = 0;
            const lastHardHitStartTime = rundown.children[lastHardHitTimeRowIndex].FrontTime;
            for (let i = lastHardHitTimeRowIndex; i < rundown.children.length; i++) {
                const story = rundown.children[i] as StorytellingStoryRecord;
                if (story.Guid === '0') {
                    break;
                }
                cumeAfterLastHardHit += getSegmentTime(story, isRoTimeEstimated, estimatedTimeThreshold).as('seconds');
            }
            const dstDiffMinutes = calculateDurationDSTDifference(datEndTime, lastHardHitStartTime);
            const timeLastHardHitToEnd = datEndTime.diff(lastHardHitStartTime).as('seconds');
            tempOverUnder = Duration.fromObject({ seconds: cumeAfterLastHardHit - timeLastHardHitToEnd }).minus({ minutes: dstDiffMinutes });
        } else {
            const dstDiffMinutes = calculateDurationDSTDifference(datEndTime, datStartTime);
            tempOverUnder = tempOverUnder.minus({ seconds: datEndTime.diff(datStartTime).as('seconds') }).minus({ minutes: dstDiffMinutes });
        }
        lastFrontTime = datStartTime;
    } else if (currentIDIndex === -1) {
        tempOverUnder = tempOverUnder.minus({ seconds: roDur });
    } else {
        if (isLtt) {
            // TODO
        } else if (isOnAir) {
            const dstDiffMinutes = calculateDurationDSTDifference(datHitTime, datEndTime);
            tempOverUnder = tempOverUnder.minus({ seconds: datEndTime.diff(datHitTime).as('seconds') }).minus({ minutes: dstDiffMinutes });
        } else {
            tempOverUnder = tempOverUnder.minus({ seconds: roDur });
        }
    }
    rundown.TimingObj = {
        ...rundown.TimingObj,
        OverUnder: tempOverUnder,
        TotalCumeTime: Duration.fromObject({ seconds: totalReadTimeCume }),
        TotalOverUnderTime: Duration.fromObject({ seconds: totalOverUnderTime }),
    };
};

export const frontTimeRow = (
    rundown: StorytellingRundownRecord,
    storyRec: StorytellingStoryRecord,
    isRoTimeEstimated: boolean,
    estimatedTimeThreshold: number,
    currentId: string,
    afterCurrentRow: boolean,
    totalReadTimeCume: number,
    hitTime: DateTime,
    totalOverUnderTime: number,
    frontHitCume: number,
    lastFrontTime: DateTime,
    lastTime: number,
    languageSettings: Record<string, string>
) => {
    if (!storyRec) return;
    if (!rundown?.children) return;

    let story = { ...storyRec };
    const datHardHitFront = story?.HardHitFront || DateTime.now();
    const tsActual = getSegmentTime(story, isRoTimeEstimated, estimatedTimeThreshold);
    const strGuid = story?.Guid;
    const isFirstStory = story?.Guid === rundown?.children?.[0]?.Guid;
    totalReadTimeCume += tsActual?.as('seconds') ?? 0;
    rundown.TimingObj = {
        ...rundown.TimingObj,
        TotalCumeTime: rundown.TimingObj?.TotalCumeTime?.plus(tsActual) ?? Duration.fromMillis(0),
    };
    story = { ...story, FrontOverUnder: Duration.fromMillis(0) };
    rundown.StartTime = rundown.StartTime.minus({ minutes: calculateDurationDSTDifference(rundown.StartTime, DateTime.now()) });
    if (!afterCurrentRow && currentId && strGuid !== currentId) {
        const storedFrontTimeProperty = story?.StoredFrontTime;
        if (storedFrontTimeProperty) {
            if ((storedFrontTimeProperty?.valueOf() || 0) >= (story.FrontTime?.valueOf() || 0)) {
                story.FrontTime = storedFrontTimeProperty;
            }
            story.FrontOverUnder = NULL_TIMESPAN;
        } else {
            story = { ...story, FrontTime: rundown.StartTime };
        }
    } else {
        afterCurrentRow = true;
    }
    if (afterCurrentRow) {
        if (isFirstStory) {
            if (isBlankLuxonDate(datHardHitFront)) {
                if (!currentId) {
                    story = { ...story, FrontTime: rundown.StartTime };
                } else {
                    story = { ...story, FrontTime: hitTime };
                }
                story = { ...story, FrontOverUnder: NULL_TIMESPAN };
                totalOverUnderTime = story.FrontTime.diff(rundown.StartTime).as('seconds');
            } else {
                const roStartTime = rundown.StartTime.set({
                    hour: datHardHitFront.hour,
                    minute: datHardHitFront.minute,
                    second: datHardHitFront.second,
                });
                const datHardHitFrontDate = roStartTime;
                const roTimeOfDay = rundown.StartTime.diff(rundown.StartTime.startOf('day'), 'seconds');
                const datHardHitFrontDateTimeOfDay = datHardHitFrontDate.diff(datHardHitFrontDate.startOf('day'), 'seconds');
                story = { ...story, FrontTime: datHardHitFrontDate };
                story = { ...story, FrontOverUnder: roTimeOfDay.plus({ seconds: frontHitCume }).minus(datHardHitFrontDateTimeOfDay) };
                totalOverUnderTime = story.FrontTime.diff(rundown.StartTime).as('seconds');
            }
        } else {
            if (isBlankLuxonDate(datHardHitFront)) {
                story = { ...story, FrontTime: lastFrontTime.plus({ seconds: lastTime }) };
                story = { ...story, FrontOverUnder: NULL_TIMESPAN };
            } else {
                const roStartTime = rundown.StartTime.set({
                    hour: datHardHitFront.hour,
                    minute: datHardHitFront.minute,
                    second: datHardHitFront.second,
                });
                const datHardHitFrontDate = roStartTime;
                story = { ...story, FrontTime: datHardHitFrontDate };
                const prevDate = lastFrontTime.plus({ seconds: lastTime });
                const tsHardHitDuration = prevDate.diff(story.FrontTime, 'seconds');
                story = { ...story, FrontOverUnder: tsHardHitDuration };
                totalOverUnderTime = totalOverUnderTime + story.FrontTime.diff(lastFrontTime, 'seconds').seconds;
            }
        }
        lastFrontTime = story?.FrontTime;
        lastTime = tsActual?.as('seconds') ?? 0;
        frontHitCume = lastTime + frontHitCume;
        totalOverUnderTime = totalOverUnderTime + (tsActual?.as('seconds') ?? 0);
    }
    story = { ...story, HardHitFrontOverUnder: getStoryHardHitOverUnderText(story?.FrontOverUnder, languageSettings) };
    story = { ...story, CumeTime: Duration.fromObject({ seconds: totalReadTimeCume }) };
    return {
        totalReadTimeCume,
        totalOverUnderTime,
        frontHitCume,
        lastFrontTime,
        lastTime,
        story,
    };
};

export const getBackTimings = (rundown: StorytellingRundownRecord, languageSettings: Record<string, string>) => {
    if (!rundown?.children) return;
    const blackLineIdx = rundown.children.findLastIndex(story => story.Guid === '0');
    const endTime = rundown.EndTime;
    let lastBackTime = DateTime.now();
    let lastBackTimeCume = 0.0;

    for (let i = blackLineIdx - 1; i >= 0; i--) {
        let story = rundown.children[i] as StorytellingStoryRecord;
        const actual = getSegmentTime(story, false, 0);

        const hardHitBack = story.HardHitBack || DateTime.now();

        if (i === blackLineIdx - 1) {
            lastBackTime = rundown.EndTime;
        }

        if (isBlankLuxonDate(hardHitBack)) {
            story = { ...story, BackTime: lastBackTime?.minus({ seconds: actual.as('seconds') }) };

            story = { ...story, BackOverUnder: NULL_TIMESPAN };
        } else {
            const hardHitEndDate = DateTime.local().set({
                year: endTime.year,
                month: endTime.month,
                day: endTime.day,
                hour: hardHitBack.hour,
                minute: hardHitBack.minute,
                second: hardHitBack.second,
            });
            story = { ...story, BackTime: hardHitEndDate };
            const prevDate = lastBackTime.minus({ seconds: actual.as('seconds') });
            const tsHardHit = story.BackTime.diff(prevDate, 'seconds');
            story = { ...story, BackOverUnder: tsHardHit };
        }
        story = { ...story, HardHitBackOverUnder: getStoryHardHitOverUnderText(story.BackOverUnder, languageSettings) };
        lastBackTimeCume += actual.as('seconds');
        story = { ...story, BackCumeTime: Duration.fromObject({ seconds: lastBackTimeCume }) };
        lastBackTime = story.BackTime;
        rundown.children[i] = story;
    }
};

export const updateSegmentTime = (timingObj: TimingObjectDuration): { isSegmentCountDown: boolean; segmentCountDown: Duration; segmentCountUp: Duration } => {
    let isSegmentCountDown = timingObj?.IsSegCountDown;
    let segmentCountDown: Duration = null;
    let segmentCountUp: Duration = null;
    if (isSegmentCountDown) {
        const segmentCurrentCountDown = timingObj?.SegmentCountDown;
        if (segmentCurrentCountDown?.as('seconds') >= 1) {
            segmentCountDown = segmentCurrentCountDown.minus({ seconds: 1 });
            if (segmentCountDown?.as('seconds') < 1) {
                isSegmentCountDown = false;
            }
        }
    } else {
        segmentCountDown = timingObj?.SegmentCountDown?.plus({ seconds: 1 });
    }

    segmentCountUp = timingObj?.SegmentCountUp?.plus({ seconds: 1 });
    return { isSegmentCountDown, segmentCountDown, segmentCountUp };
};

export const updateStoryTime = (timingObj: TimingObjectDuration): { isStoryCountDown: boolean; storyCountDown: Duration } => {
    let isStoryCountDown = timingObj?.IsStoryCountDown;
    let storyCountDown: Duration = null;
    if (isStoryCountDown) {
        const storyCurrentCountDown = timingObj?.StoryCountDown;
        if (storyCurrentCountDown?.as('seconds') >= 1) {
            storyCountDown = storyCurrentCountDown.minus({ seconds: 1 });
            if (storyCountDown?.as('seconds') < 1) {
                isStoryCountDown = false;
            }
        }
    } else {
        storyCountDown = timingObj?.StoryCountDown?.plus({ seconds: 1 });
    }

    return { isStoryCountDown, storyCountDown };
};

export const updateProgramTime = (timingObj: TimingObjectDuration): { isProgramCountDown: boolean; programCountDown: Duration } => {
    let isProgramCountDown = timingObj?.IsROCountDown;
    let programCountDown = timingObj?.ProgramCountDown;
    if (isProgramCountDown) {
        if (programCountDown?.as('seconds') >= 1) {
            programCountDown = programCountDown.minus({ seconds: 1 });
            if (programCountDown?.as('seconds') < 1) {
                isProgramCountDown = false;
            }
        }
    } else {
        programCountDown = programCountDown?.plus({ seconds: 1 });
    }

    return { isProgramCountDown, programCountDown };
};

export const updateRoClocks = (rundown: StorytellingRundownRecord, isRoTimeEstimated: boolean, estimatedTimeThreshold: number) => {
    if (!rundown?.children) return;
    const onAir = rundown?.OnAir;
    const isLtt = rundown?.LTTRecording;
    const isLttPaused = rundown?.LTTPaused;
    if (!onAir && !isLtt) {
        return rundown.TimingObj;
    }

    const currentId = rundown?.CurrentID;
    const currentStory = rundown?.children?.find(story => story.Guid === currentId);
    const startTime = rundown?.StartTime;
    const endTime = rundown?.EndTime;
    const isFixedDuration = rundown?.FixedDuration;
    let hitTime = rundown?.HitTime;
    let newDurationTimespan = Duration.fromMillis(0);
    const ret = { ...rundown.TimingObj };

    if (!hitTime || isBlankLuxonDate(hitTime)) {
        hitTime = DateTime.now();
    }
    if (!ret?.IsTiming || !ret?.ProgramRan) {
        const now = DateTime.now();
        if (isFixedDuration) {
            const elapsedTime = now.diff(startTime, 'seconds');
            const totalDuration = endTime.diff(startTime, 'seconds');
            newDurationTimespan = totalDuration.minus(elapsedTime);
            ret.IsROCountDown = newDurationTimespan.as('seconds') > 0;
        } else {
            newDurationTimespan = now.diff(endTime, 'seconds');
            newDurationTimespan = newDurationTimespan.minus({
                minutes: calculateDurationDSTDifference(endTime, now),
            });
            ret.IsROCountDown = newDurationTimespan.as('seconds') <= 0;
        }
        newDurationTimespan = newDurationTimespan.shiftTo('hours', 'minutes', 'seconds');
        ret.ProgramCountDown = newDurationTimespan;
        ret.IsTiming = true;
        ret.ProgramRan = true;
    }

    let tsSegment = Duration.fromMillis(0);
    const tsSegCountUp = DateTime.now().diff(hitTime);
    const tsSegCountUp2 = tsSegCountUp.shiftTo('hours', 'minutes', 'seconds');
    if (!isLttPaused) {
        ret.SegmentCountUp = tsSegCountUp2;
    } else {
        ret.SegmentCountUp = Duration.fromMillis(0);
    }

    tsSegment = getSegmentTime(currentStory, isRoTimeEstimated, estimatedTimeThreshold);
    const tsGetSetDown = tsSegCountUp2.minus(tsSegment);
    if (tsSegment.as('seconds') === 0 || tsGetSetDown.as('milliseconds') > tsSegment.as('milliseconds')) {
        ret.IsSegCountDown = false;
    } else {
        ret.IsSegCountDown = true;
    }
    if (!isLttPaused) {
        ret.SegmentCountDown = Duration.fromMillis(Math.abs(tsGetSetDown.valueOf()));
    } else {
        ret.SegmentCountDown = Duration.fromMillis(Math.abs(tsSegment.valueOf()));
    }

    const tsStory = getStoryDuration(rundown, currentId, isRoTimeEstimated, estimatedTimeThreshold);
    if (!isLttPaused) {
        const tsGetStory = tsSegCountUp2.minus(tsStory);
        ret.IsStoryCountDown = tsStory.as('seconds') !== 0 && tsGetStory.as('seconds') < 0;
        ret.StoryCountDown = Duration.fromMillis(Math.abs(tsGetStory.valueOf()));
    } else {
        ret.StoryCountDown = Duration.fromMillis(Math.abs(tsStory.valueOf()));
    }

    return ret;
};

export const getOverUnderText = (timingObject: TimingObjectDuration, languageSettings: Record<string, string>): string => {
    if (timingObject == null || timingObject?.OverUnder == null) return '';

    const overUnder: Duration | undefined = timingObject?.OverUnder;
    const overUnderDur = overUnder ?? Duration.fromMillis(0);
    const zeroDuration = Duration.fromMillis(0);

    if (overUnderDur > zeroDuration) {
        const formatted = formatDurationLikeMoment(overUnderDur);
        return `${languageSettings?.RDTimedOver} ${formatted}`;
    } else if (overUnderDur < zeroDuration) {
        const formatted = formatDurationLikeMoment(overUnderDur);
        return `${languageSettings?.RDTimedUnder} ${formatted}`;
    } else {
        return languageSettings.RDTimedOK;
    }
};

export const getHardHitOverUnderText = (ts: Duration, languageSettings: Record<string, string>): string => {
    if (!ts) return '';

    const overUnder: Duration | undefined = ts;
    const overUnderDur = overUnder ?? Duration.fromMillis(0);
    const zeroDuration = Duration.fromMillis(0);

    if (overUnderDur > zeroDuration) {
        const formatted = formatDurationLikeMoment(overUnderDur);
        return `${languageSettings?.RDHardHitTime} ${languageSettings?.RDTimedOver} ${formatted}`;
    } else if (overUnderDur < zeroDuration) {
        const formatted = formatDurationLikeMoment(overUnderDur);
        return `${languageSettings?.RDHardHitTime} ${languageSettings?.RDTimedUnder} ${formatted}`;
    } else {
        return `${languageSettings?.RDHardHitTime}} ${languageSettings?.RDTimedOK}`;
    }
};

export const getStoryHardHitOverUnderText = (ts: Duration, languageSettings: Record<string, string>): string => {
    if (!ts) return '';

    const overUnder: Duration | undefined = ts;
    const overUnderDur = overUnder ?? Duration.fromMillis(0);
    const zeroDuration = Duration.fromMillis(0);
    if (overUnder === NULL_TIMESPAN) return '';
    if (overUnderDur > zeroDuration) {
        const formatted = formatDurationLikeMoment(overUnderDur);
        return `${languageSettings?.RDTimedOver} ${formatted}`;
    } else if (overUnderDur < zeroDuration) {
        const formatted = formatDurationLikeMoment(overUnderDur);
        return `${languageSettings?.RDTimedUnder} ${formatted}`;
    } else {
        return languageSettings?.RDTimedOK;
    }
};

export const calculateDuration = (startTime: DateTime, endTime: DateTime) => {
    const startTimeMoment = startTime;
    const endTimeMoment = endTime;
    // need to format it like "30:00"
    if (startTimeMoment && endTimeMoment) {
        const durationMinutes = endTimeMoment.diff(startTimeMoment, 'minutes').as('minutes');
        return Duration.fromObject({ minutes: durationMinutes });
    }
    return null;
};
